import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import {
  Paper, TablePagination, TextField, Button, Box, Typography,
  Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert,
  useTheme, useMediaQuery, Grid, Tooltip, IconButton,
  Drawer, Badge, Switch, List, ListItem, ListItemIcon, ListItemText,
  ListItemSecondaryAction, Divider, CircularProgress, Checkbox, Radio,
  RadioGroup, FormControlLabel,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Search as SearchIcon,
  Add as AddIcon,
  ViewColumn as ViewColumnIcon,
  DragIndicator as DragIndicatorIcon,
  History as HistoryIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { formatDateTime } from '../utils/dateFormat';
import CertificateLink from '../components/CertificateLink';
import ResponsiveTable, { VIEWPORT_TABLE_MAX_HEIGHT } from '../components/ResponsiveTable';
import { useAuth } from '../context/AuthContext';
import CertifiedDiamondCartAction from '../components/inventory/CertifiedDiamondCartAction';
import { certifiedInventoryPropsAreEqual } from '../utils/inventoryTabMemo';
import { useFeedback } from '../context/FeedbackContext';
import { useInventoryShell } from '../context/InventoryShellContext';
import { ROLES } from '../constants/roles';
import {
  formatGradeShort,
  formatFancyColor,
  FANCY_COLOR_HUE_LIST,
} from '../constants/appConstants';
import axios from 'axios';
import { TABLE_ROWS_PREF_KEYS, pickRowsPerPageFromPrefs, saveTableRowsPreference } from '../utils/tablePagePreferences';
import { parseCertifiedSearchParams, buildCertifiedSearchParams, certifiedSortColumnToApiField, isCertifiedSortColumn, DEFAULT_INTERNAL_CERTIFIED_SORT, DEFAULT_CLIENT_CERTIFIED_SORT, normalizeCertifiedSortColumn } from '../utils/inventoryUrlParams';
import AuditLogDialog from '../components/inventory/AuditLogDialog';
import CertifiedFilterDrawer, {
  DEFAULT_CERTIFIED_FILTERS,
  DEFAULT_CERTIFIED_ADVANCED_FILTERS,
} from '../components/inventory/CertifiedFilterDrawer';
import UploadCsvDialog from '../components/inventory/UploadCsvDialog';
import EditDiamondDialog from '../components/inventory/EditDiamondDialog';
import AddDiamondDialog from '../components/inventory/AddDiamondDialog';
import QuickFilterBar from '../components/inventory/QuickFilterBar';
import { useTranslation } from 'react-i18next';
import SelectField from '../components/inputs/SelectField';
import { resolveInventorySettings, validateClientCaratRange, resolveClientShapeSelection, resolveClientDefaultShapeFilter } from '../utils/inventorySettings';
import { resolveShapeOptions, inventoryShapeOptions } from '../utils/shapeOptions';
import { hasPermission } from '../utils/permissions';

// ── Module-level static data (never re-created on render) ────────────────────

const DIAMOND_STATUS_OPTIONS = [
  { value: 'available', label: 'Available' },
  { value: 'reserved', label: 'Reserved' },
  { value: 'sold', label: 'Sold' },
  { value: 'onTheWay', label: 'On the Way' },
  { value: 'notForSell', label: 'Not for sell' },
  { value: 'deleted', label: 'Deleted' },
];

const ALL_COLUMNS = [
  { id: 'certificateNumber', label: 'Certificate #', alwaysOn: false },
  { id: 'sku', label: 'SKU', internalOnly: true },
  { id: 'shipmentId', label: 'Shipment ID', internalOnly: true },
  { id: 'shipmentItemID', label: 'Shipment Item ID', internalOnly: true },
  { id: 'shape', label: 'Shape' },
  { id: 'carat', label: 'Carat', sortable: true },
  { id: 'color', label: 'Color' },
  { id: 'clarity', label: 'Clarity' },
  { id: 'price', label: 'Price', sortable: true },
  { id: 'totalPrice', label: 'Total Price' },
  { id: 'cut', label: 'Cut' },
  { id: 'polish', label: 'Polish' },
  { id: 'symmetry', label: 'Symmetry' },
  { id: 'lab', label: 'Lab' },
  { id: 'diamondType', label: 'Type' },
  { id: 'measurements', label: 'Measurements (mm)' },
  { id: 'ratio', label: 'Ratio (L/W)' },
  { id: 'buyPricePerCarat', label: 'Buy price (per ct)', internalOnly: true, requiresPermission: 'see_buy_prices' },
  { id: 'status', label: 'Status', internalOnly: true },
  { id: 'byWhom', label: 'By whom', internalOnly: true },
  { id: 'dateAdded', label: 'Date added', internalOnly: true },
  { id: 'dateUpdated', label: 'Date updated', internalOnly: true },
  { id: 'reserveAction', label: 'Actions', alwaysOn: true },
];

const CARD_LABEL_SX = { fontSize: '0.8rem', lineHeight: 1.2, display: 'block' };
const CARD_VALUE_SX = { fontSize: '0.9rem', lineHeight: 1.2, fontWeight: 500 };

function getStatusColor(status) {
  switch (status) {
    case 'available': return 'success';
    case 'reserved': return 'warning';
    case 'sold': return 'default';
    case 'onTheWay': return 'info';
    case 'notForSell': return 'default';
    default: return 'default';
  }
}

function formatRatio(row) {
  if (row.ratio != null) return Number(row.ratio).toFixed(2);
  if (row.length != null && row.width != null && Number(row.width) > 0) {
    return (Math.round((row.length / row.width) * 100) / 100).toFixed(2);
  }
  return '—';
}

function formatMeasurements(row) {
  if (row.length != null && row.width != null && row.height != null) {
    return `${row.length} × ${row.width} × ${row.height}`;
  }
  return '—';
}

function formatByWhom(row) {
  if (row.status === 'sold' && row.soldByUser) return `${row.soldByUser} → ${row.soldToCompany || '—'}`;
  if (row.status === 'reserved' && row.reservedByUser) return `${row.reservedByUser} (${row.reservedForCompany || '—'})`;
  if (row.requestedByUser) return `Requested: ${row.requestedByUser} (${row.requestedForCompany || '—'})`;
  return '—';
}

// ── Component ────────────────────────────────────────────────────────────────

const CertifiedInventory = ({ colorCategory = 'standard', isTabActive = true, searchParams, setSearchParams }) => {
  const isFancyInventory = colorCategory === 'fancy';
  const { t } = useTranslation();
  const { isInternal, isClient, userRole, customPermissions, company, loading: authLoading } = useAuth();
  const inventorySettings = resolveInventorySettings(company);
  const certifiedSettings = inventorySettings.certified;
  const isManager = userRole === ROLES.MANAGER;
  const canSeeBuyPrices = hasPermission(userRole, 'see_buy_prices', customPermissions);
  const { showError, showSuccess } = useFeedback();
  const { filterOptions } = useInventoryShell();
  const certifiedInventoryShapes = filterOptions.certifiedShapes ?? filterOptions.shapes;
  const clientShapeList = useMemo(
    () => inventoryShapeOptions(certifiedInventoryShapes),
    [certifiedInventoryShapes],
  );
  const filterShapeList = clientShapeList;
  const formShapeList = useMemo(
    () => resolveShapeOptions(certifiedInventoryShapes, company?.customShapes),
    [certifiedInventoryShapes, company?.customShapes],
  );
  const configuredClientDefault = useMemo(
    () => (isClient ? resolveClientDefaultShapeFilter(company, 'certified') : ''),
    [isClient, company],
  );
  const clientDefaultShape = useMemo(
    () => (isClient ? resolveClientShapeSelection(configuredClientDefault, clientShapeList) : ''),
    [isClient, configuredClientDefault, clientShapeList],
  );
  const filterOptionsForUi = useMemo(
    () => ({ ...filterOptions, shapes: filterShapeList }),
    [filterOptions, filterShapeList],
  );

  const fancyHueFilterOptions = useMemo(() => {
    const fromApi = filterOptions.fancyHues || [];
    const list = fromApi.length > 0 ? fromApi : [...FANCY_COLOR_HUE_LIST];
    return list.map((h) => ({ value: h, label: h }));
  }, [filterOptions.fancyHues]);

  const canAddToCart = isInternal
    ? (userRole === ROLES.MANAGER || (customPermissions && customPermissions.includes('reserve_for_client')))
    : userRole === ROLES.CLIENT;
  const canEditDiamond = isInternal && (userRole === ROLES.MANAGER || (customPermissions && customPermissions.includes('edit_diamond')));
  const canAddDiamond = isInternal && (userRole === ROLES.MANAGER || (customPermissions && customPermissions.includes('add_diamond')));
  const canDeleteDiamond = isInternal && (userRole === ROLES.MANAGER || (customPermissions && customPermissions.includes('delete_diamond')));

  const colLabel = useCallback((id) => {
    if (id === 'color' && isFancyInventory) return t('inventory.fancyColor');
    return t(`inventory.col.${id}`, ALL_COLUMNS.find(c => c.id === id)?.label || id);
  }, [t, isFancyInventory]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const skipUrlHydrationRef = useRef(false);
  const suppressListFetchRef = useRef(false);
  const defaultsAppliedRef = useRef(false);
  const fetchSeqRef = useRef(0);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);

  // ── Certified diamonds state ─────────────────────────────────────────────
  const [diamonds, setDiamonds] = useState([]);
  const [diamondsLoading, setDiamondsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 20 : 50);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ ...DEFAULT_CERTIFIED_FILTERS });
  const [advancedFilters, setAdvancedFilters] = useState({ ...DEFAULT_CERTIFIED_ADVANCED_FILTERS });

  // Snapshot of filters that were actually used in the last Search call.
  // Chips are derived from this — not from the live drawer state — so they
  // only appear after the user explicitly clicks Search.
  const [appliedFilters, setAppliedFilters] = useState({ ...DEFAULT_CERTIFIED_FILTERS });
  const [appliedAdvancedFilters, setAppliedAdvancedFilters] = useState({ ...DEFAULT_CERTIFIED_ADVANCED_FILTERS });

  const [sortBy, setSortBy] = useState(() => (isInternal ? DEFAULT_INTERNAL_CERTIFIED_SORT : DEFAULT_CLIENT_CERTIFIED_SORT));
  const [sortOrder, setSortOrder] = useState('desc');

  // ── Audit log ─────────────────────────────────────────────────────────────
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditTitle, setAuditTitle] = useState('');

  // ── Edit / Add / Delete diamond ───────────────────────────────────────────
  const [editDiamondId, setEditDiamondId] = useState(null);
  const [addDiamondDialogOpen, setAddDiamondDialogOpen] = useState(false);
  const [deleteDiamondId, setDeleteDiamondId] = useState(null);
  const [deleteDiamondConfirmOpen, setDeleteDiamondConfirmOpen] = useState(false);
  const [deleteDiamondLoading, setDeleteDiamondLoading] = useState(false);
  const [deleteDiamondError, setDeleteDiamondError] = useState('');

  // ── Bulk edit ─────────────────────────────────────────────────────────────
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkSelectedIds, setBulkSelectedIds] = useState([]);
  const [bulkEditStep, setBulkEditStep] = useState(1);
  const [bulkEditUseFilter, setBulkEditUseFilter] = useState(false);
  const [bulkEditCount, setBulkEditCount] = useState(0);
  const [bulkEditField, setBulkEditField] = useState('');
  const [bulkEditValue, setBulkEditValue] = useState('');
  const [bulkEditConfirmOpen, setBulkEditConfirmOpen] = useState(false);
  const [bulkEditLoading, setBulkEditLoading] = useState(false);
  const [bulkEditError, setBulkEditError] = useState('');

  const [mobileExpandedCardIds, setMobileExpandedCardIds] = useState(() => new Set());
  const toggleMobileCardExpanded = useCallback((id) => {
    setMobileExpandedCardIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // ── Column configuration + drawer ─────────────────────────────────────────
  const [columnDrawerOpen, setColumnDrawerOpen] = useState(false);
  const [columnConfig, setColumnConfig] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);

  // Local input value for the cert# field — only merged into `filters` when
  // the user clicks Search, so keystrokes never touch the main filter state
  // and therefore never trigger expensive cascading memo invalidations.
  const [certNumberInput, setCertNumberInput] = useState('');

  // ── Upload CSV dialog ─────────────────────────────────────────────────────
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  const [qfShape, setQfShape] = useState('');
  const [qfWeight, setQfWeight] = useState('');
  const [qfLength, setQfLength] = useState('');

  // ── Column config helpers ──────────────────────────────────────────────────

  const getDefaultColumnConfig = useCallback(() => {
    const columnAllowed = (c) => {
      if (c.internalOnly && !isInternal) return false;
      if (c.managerOnly && !isManager) return false;
      if (c.requiresPermission && !hasPermission(userRole, c.requiresPermission, customPermissions)) return false;
      return true;
    };
    const available = ALL_COLUMNS.filter(columnAllowed);
    return { visibleIds: available.map(c => c.id), order: available.map(c => c.id) };
  }, [isInternal, isManager, userRole, customPermissions]);

  // Load preferences from server on mount
  useEffect(() => {
    const loadPreferences = async () => {
      let prefs = {};
      try {
        const response = await axios.get('/api/users/preferences');
        prefs = response.data || {};
        if (prefs.inventoryColumns) {
          const saved = prefs.inventoryColumns;
          const available = ALL_COLUMNS.filter((c) => {
            if (c.internalOnly && !isInternal) return false;
            if (c.managerOnly && !isManager) return false;
            if (c.requiresPermission && !hasPermission(userRole, c.requiresPermission, customPermissions)) return false;
            return true;
          });
          const availableIds = available.map(c => c.id);
          const validOrder = (saved.order || []).filter(id => availableIds.includes(id));
          const missingIds = availableIds.filter(id => !validOrder.includes(id));
          const order = [...validOrder, ...missingIds];
          const validVisible = [...new Set([...(saved.visibleIds || []).filter(id => availableIds.includes(id)), ...missingIds])];
          setColumnConfig({ visibleIds: validVisible, order });
        } else {
          setColumnConfig(getDefaultColumnConfig());
        }
      } catch {
        setColumnConfig(getDefaultColumnConfig());
      }
      setRowsPerPage(pickRowsPerPageFromPrefs(prefs, TABLE_ROWS_PREF_KEYS.inventoryCertified, [10, 20, 50, 100], isMobile ? 20 : 50));
      setPreferencesLoaded(true);
    };
    loadPreferences();
  }, [isInternal, isManager, isMobile, getDefaultColumnConfig, userRole, customPermissions]);

  const saveColumnPreference = async (config) => {
    try {
      await axios.put('/api/users/preferences', { key: 'inventoryColumns', value: config });
    } catch (err) {
      console.error('Failed to save column preference:', err);
    }
  };

  const handleToggleColumn = (colId) => {
    setColumnConfig(prev => {
      const isVisible = prev.visibleIds.includes(colId);
      const newVisible = isVisible ? prev.visibleIds.filter(id => id !== colId) : [...prev.visibleIds, colId];
      const newConfig = { ...prev, visibleIds: newVisible };
      saveColumnPreference(newConfig);
      return newConfig;
    });
  };

  const handleDragStart = (colId) => setDragIdx(colId);

  const handleDragOver = (e, targetColId) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === targetColId) return;
    setColumnConfig(prev => {
      const newOrder = [...prev.order];
      const fromIdx = newOrder.indexOf(dragIdx);
      const toIdx = newOrder.indexOf(targetColId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      newOrder.splice(fromIdx, 1);
      newOrder.splice(toIdx, 0, dragIdx);
      return { ...prev, order: newOrder };
    });
  };

  const handleDragEnd = () => {
    setDragIdx(null);
    if (columnConfig) saveColumnPreference(columnConfig);
  };

  const handleResetColumns = () => {
    const def = getDefaultColumnConfig();
    setColumnConfig(def);
    saveColumnPreference(def);
  };

  // ── URL snapshot ──────────────────────────────────────────────────────────

  const getUrlSnapshot = useCallback(() => ({
    filters, advancedFilters, sortBy, sortOrder, page, rowsPerPage,
    isInternal, defaultRows: isMobile ? 20 : 50,
  }), [filters, advancedFilters, sortBy, sortOrder, page, rowsPerPage, isInternal, isMobile]);

  // ── Data fetching ─────────────────────────────────────────────────────────

  const fetchDiamonds = useCallback(async (opts = {}) => {
    const {
      pageOverride,
      filtersOverride,
      advancedFiltersOverride,
      sortByOverride,
      sortOrderOverride,
    } = opts;
    const pageToUse = pageOverride !== undefined ? pageOverride : page;
    const filtersToUse = filtersOverride ?? filters;
    const advancedToUse = advancedFiltersOverride ?? advancedFilters;
    const sortByToUse = sortByOverride !== undefined ? sortByOverride : sortBy;
    const sortOrderToUse = sortOrderOverride !== undefined ? sortOrderOverride : sortOrder;
    const fetchSeq = ++fetchSeqRef.current;
    setDiamondsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', (pageToUse + 1).toString());
      params.set('limit', rowsPerPage.toString());
      params.set('colorCategory', colorCategory);
      Object.entries(filtersToUse).forEach(([key, value]) => {
        if (key === 'status' && !isInternal) return;
        if (key === 'certificateNumber' && !isInternal) return;
        if (key === 'shipmentId' && !isInternal) return;
        if (key === 'shipmentItemID' && !isInternal) return;
        if (key === 'sku' && !isInternal) return;
        if (key === 'color' && isFancyInventory) return;
        if ((key === 'fancyIntensity' || key === 'fancyHue' || key === 'fancyOvertone') && !isFancyInventory) return;
        if (key === 'status' && isInternal && Array.isArray(value) && value.length === 0) return;
        if (value === '' || (Array.isArray(value) && value.length === 0)) return;
        if (Array.isArray(value)) params.set(key, value.join(','));
        else params.set(key, value);
      });
      Object.entries(advancedToUse).forEach(([key, value]) => {
        if (value === '') return;
        params.set(key, value);
      });
      const sortByParam = certifiedSortColumnToApiField(sortByToUse);
      if (sortByParam) params.set('sortBy', sortByParam);
      if (sortOrderToUse) params.set('sortOrder', sortOrderToUse);
      const response = await axios.get(`/api/diamonds?${params}`);
      if (fetchSeq !== fetchSeqRef.current) return;
      setDiamonds(response.data.diamonds ?? []);
      setTotal(response.data.pagination?.total ?? 0);
    } catch (error) {
      if (fetchSeq !== fetchSeqRef.current) return;
      console.error('Failed to fetch diamonds:', error);
    } finally {
      if (fetchSeq === fetchSeqRef.current) {
        setDiamondsLoading(false);
      }
    }
  }, [page, rowsPerPage, filters, advancedFilters, sortBy, sortOrder, isInternal, colorCategory, isFancyInventory]);

  const handleSearch = useCallback((filtersOverride, advancedOverride) => {
    const filtersToApply = filtersOverride ?? filters;
    const advancedToApply = advancedOverride ?? advancedFilters;
    const effectiveFilters = { ...filtersToApply, certificateNumber: certNumberInput };

    if (isClient) {
      if (certifiedSettings.requireShapeFilter && !effectiveFilters.shape) {
        showError(t('settings.inventory.certified.requireShapePrompt'));
        return;
      }
      const rangeCheck = validateClientCaratRange(
        effectiveFilters.minCarat,
        effectiveFilters.maxCarat,
        inventorySettings,
      );
      if (!rangeCheck.ok) {
        showError(t('settings.inventory.certified.caratRangeTooWide', { span: rangeCheck.span }));
        return;
      }
    }

    setFilters(effectiveFilters);
    setAppliedFilters(effectiveFilters);
    setAdvancedFilters(advancedToApply);
    setAppliedAdvancedFilters(advancedToApply);
    setQfShape(effectiveFilters.shape || '');
    setPage(0);
    const snap = {
      filters: effectiveFilters,
      advancedFilters: advancedToApply,
      sortBy,
      sortOrder,
      page: 0,
      rowsPerPage,
      isInternal,
      defaultRows: isMobile ? 20 : 50,
    };
    skipUrlHydrationRef.current = true;
    setSearchParams(buildCertifiedSearchParams(snap), { replace: true });
    fetchDiamonds({
      pageOverride: 0,
      filtersOverride: effectiveFilters,
      advancedFiltersOverride: advancedToApply,
    });
  }, [certNumberInput, filters, advancedFilters, sortBy, sortOrder, rowsPerPage, isInternal, isMobile, isClient, certifiedSettings, inventorySettings, fetchDiamonds, setSearchParams, showError, t]);

  // ── Quick filter ──────────────────────────────────────────────────────────

  const handleQuickCertFilterApply = useCallback(({ shape, weightCenter, minWeight, maxWeight, lengthCenter, minLength, maxLength }) => {
    setQfShape(shape);
    setQfWeight(weightCenter);
    setQfLength(lengthCenter);
    const newFilters = { ...appliedFilters, shape, minCarat: minWeight, maxCarat: maxWeight };
    const newAdvanced = { ...appliedAdvancedFilters, minLength, maxLength };
    setFilters(newFilters);
    setAdvancedFilters(newAdvanced);
    setAppliedFilters(newFilters);
    setAppliedAdvancedFilters(newAdvanced);
    setPage(0);
    fetchDiamonds({ pageOverride: 0, filtersOverride: newFilters, advancedFiltersOverride: newAdvanced });
  }, [appliedFilters, appliedAdvancedFilters, fetchDiamonds]);

  const handleQuickCertFilterClear = useCallback(() => {
    const resetShape = isClient ? clientDefaultShape : '';
    setQfShape(resetShape);
    setQfWeight('');
    setQfLength('');
    const newFilters = { ...appliedFilters, shape: resetShape, minCarat: '', maxCarat: '' };
    const newAdvanced = { ...appliedAdvancedFilters, minLength: '', maxLength: '' };
    setFilters(newFilters);
    setAdvancedFilters(newAdvanced);
    setAppliedFilters(newFilters);
    setAppliedAdvancedFilters(newAdvanced);
    setPage(0);
    fetchDiamonds({ pageOverride: 0, filtersOverride: newFilters, advancedFiltersOverride: newAdvanced });
  }, [appliedFilters, appliedAdvancedFilters, fetchDiamonds, isClient, clientDefaultShape]);

  const handleFancyHueFilterChange = useCallback((nextHue) => {
    const newFilters = { ...appliedFilters, fancyHue: nextHue };
    setFilters(newFilters);
    setAppliedFilters(newFilters);
    setPage(0);
    const snap = {
      filters: newFilters,
      advancedFilters: appliedAdvancedFilters,
      sortBy,
      sortOrder,
      page: 0,
      rowsPerPage,
      isInternal,
      defaultRows: isMobile ? 20 : 50,
    };
    skipUrlHydrationRef.current = true;
    setSearchParams(buildCertifiedSearchParams(snap), { replace: true });
    fetchDiamonds({ pageOverride: 0, filtersOverride: newFilters });
  }, [
    appliedFilters,
    appliedAdvancedFilters,
    sortBy,
    sortOrder,
    rowsPerPage,
    isInternal,
    isMobile,
    fetchDiamonds,
    setSearchParams,
  ]);

  // Fetch when page, rowsPerPage, or sort change (filter changes use Search button)
  useEffect(() => {
    if (!isTabActive || !preferencesLoaded || authLoading) return;
    if (isClient && !defaultsAppliedRef.current) return;
    if (suppressListFetchRef.current) { suppressListFetchRef.current = false; return; }
    fetchDiamonds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, sortBy, sortOrder, preferencesLoaded, authLoading, isTabActive]);

  // Deep-link / back-forward URL hydration
  useLayoutEffect(() => {
    if (!isTabActive) return;
    if (skipUrlHydrationRef.current) { skipUrlHydrationRef.current = false; return; }
    if (!searchParams.toString()) return;
    const parsed = parseCertifiedSearchParams(searchParams, isInternal);
    suppressListFetchRef.current = true;
    setFilters(parsed.filters);
    setCertNumberInput(parsed.filters.certificateNumber || '');
    setAdvancedFilters(parsed.advancedFilters);
    setAppliedFilters(parsed.filters);
    setAppliedAdvancedFilters(parsed.advancedFilters);
    setQfShape(parsed.filters.shape || '');
    if (parsed.rowsPerPage != null) setRowsPerPage(parsed.rowsPerPage);
    setPage(parsed.page);
    setSortBy(parsed.sortBy);
    setSortOrder(parsed.sortOrder);
    queueMicrotask(() => {
      fetchDiamonds({
        pageOverride: parsed.page,
        filtersOverride: parsed.filters,
        advancedFiltersOverride: parsed.advancedFilters,
        sortByOverride: parsed.sortBy,
        sortOrderOverride: parsed.sortOrder,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isInternal, isTabActive]);

  // Re-apply when company default shape settings refresh (e.g. after /api/auth/me)
  useEffect(() => {
    if (isClient) defaultsAppliedRef.current = false;
  }, [isClient, configuredClientDefault]);

  // Apply company default shape for clients on first load
  useEffect(() => {
    if (!isTabActive || !preferencesLoaded || authLoading) return;

    const urlShape = searchParams.toString()
      ? parseCertifiedSearchParams(searchParams, isInternal).filters.shape
      : '';

    if (!isClient) {
      defaultsAppliedRef.current = true;
      return;
    }

    if (urlShape) {
      defaultsAppliedRef.current = true;
      return;
    }

    if (defaultsAppliedRef.current) return;

    if (!configuredClientDefault && clientShapeList.length === 0) return;

    const targetShape = resolveClientShapeSelection(configuredClientDefault, clientShapeList);
    if (!targetShape) return;

    defaultsAppliedRef.current = true;
    const newFilters = { ...DEFAULT_CERTIFIED_FILTERS, shape: targetShape };
    suppressListFetchRef.current = true;
    setFilters(newFilters);
    setAppliedFilters(newFilters);
    setQfShape(targetShape);
    setPage(0);
    fetchDiamonds({ pageOverride: 0, filtersOverride: newFilters });
  }, [
    isTabActive,
    preferencesLoaded,
    authLoading,
    isClient,
    isInternal,
    configuredClientDefault,
    clientShapeList,
    searchParams,
    fetchDiamonds,
  ]);

  // ── Filter handlers ───────────────────────────────────────────────────────

  const handleClearFilters = useCallback(() => {
    const resetShape = isClient ? clientDefaultShape : '';
    setFilters({ ...DEFAULT_CERTIFIED_FILTERS, shape: resetShape });
    setAdvancedFilters({ ...DEFAULT_CERTIFIED_ADVANCED_FILTERS });
    setAppliedFilters({ ...DEFAULT_CERTIFIED_FILTERS, shape: resetShape });
    setAppliedAdvancedFilters({ ...DEFAULT_CERTIFIED_ADVANCED_FILTERS });
    setCertNumberInput('');
    setQfShape(resetShape);
    setQfWeight('');
    setQfLength('');
  }, [isClient, clientDefaultShape]);

  const handleOpenBulkEditFromFilters = useCallback(() => {
    setBulkEditOpen(true);
    setBulkEditStep(1);
    setBulkEditConfirmOpen(false);
    setBulkEditError('');
  }, []);

  // ── Sort handler ──────────────────────────────────────────────────────────

  const handleSort = useCallback((columnId, direction) => {
    const normalizedColumnId = normalizeCertifiedSortColumn(columnId, isInternal);
    let nextSortOrder;
    if (direction) {
      nextSortOrder = direction;
    } else if (sortBy === normalizedColumnId) {
      nextSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      nextSortOrder = 'asc';
    }
    suppressListFetchRef.current = true;
    setSortBy(normalizedColumnId);
    setSortOrder(nextSortOrder);
    setPage(0);
    fetchDiamonds({
      pageOverride: 0,
      sortByOverride: normalizedColumnId,
      sortOrderOverride: nextSortOrder,
    });
    queueMicrotask(() => {
      skipUrlHydrationRef.current = true;
      setSearchParams(
        buildCertifiedSearchParams({ ...getUrlSnapshot(), page: 0, sortBy: normalizedColumnId, sortOrder: nextSortOrder }),
        { replace: true },
      );
    });
  }, [sortBy, sortOrder, getUrlSnapshot, setSearchParams, fetchDiamonds, isInternal]);

  // ── Audit log ─────────────────────────────────────────────────────────────

  const openAuditLog = useCallback(async (type, id, title) => {
    setAuditTitle(title);
    setAuditDialogOpen(true);
    setAuditLogs([]);
    setAuditLoading(true);
    try {
      const params = { limit: 100 };
      if (type === 'diamond') params.diamond = id;
      else if (type === 'lot') params.lot = id;
      const res = await axios.get('/api/audit-logs', { params });
      setAuditLogs(res.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setAuditLoading(false);
    }
  }, []);

  // ── Diamond CRUD ──────────────────────────────────────────────────────────

  const openEditDiamond = useCallback((diamondId) => {
    setEditDiamondId(diamondId);
  }, []);

  const closeEditDiamond = useCallback(() => setEditDiamondId(null), []);

  const handleAddDiamondSaved = useCallback(() => {
    fetchDiamonds();
    showSuccess('Diamond added');
  }, [fetchDiamonds, showSuccess]);

  const openDeleteDiamond = useCallback((diamondId) => {
    setDeleteDiamondId(diamondId); setDeleteDiamondConfirmOpen(true);
  }, []);

  const closeDeleteDiamond = () => {
    setDeleteDiamondConfirmOpen(false); setDeleteDiamondId(null); setDeleteDiamondError('');
  };

  const handleConfirmDeleteDiamond = async () => {
    if (!deleteDiamondId) return;
    if (deleteDiamondLoading) return;
    setDeleteDiamondLoading(true);
    setDeleteDiamondError('');
    try {
      await axios.delete(`/api/diamonds/${deleteDiamondId}`);
      closeDeleteDiamond();
      fetchDiamonds();
      showSuccess('Diamond removed');
    } catch (err) {
      setDeleteDiamondError(err.response?.data?.error || 'Failed to delete diamond');
    } finally {
      setDeleteDiamondLoading(false);
    }
  };

  // ── Bulk edit ─────────────────────────────────────────────────────────────

  const handleBulkEditUseFilter = () => {
    setBulkEditUseFilter(true); setBulkEditCount(total); setBulkEditStep(2); setBulkEditError('');
  };
  const handleBulkEditNext = () => {
    if (bulkSelectedIds.length === 0) { setBulkEditError('Select at least one item or use "Update all by filter".'); return; }
    setBulkEditUseFilter(false); setBulkEditCount(bulkSelectedIds.length); setBulkEditStep(2); setBulkEditError('');
  };
  const handleBulkEditFieldConfirm = () => {
    if (!bulkEditField || (bulkEditField === 'basePrice' && (bulkEditValue === '' || Number(bulkEditValue) < 0)) || (bulkEditField === 'status' && !bulkEditValue)) return;
    setBulkEditConfirmOpen(true);
  };
  const handleBulkEditSubmit = async () => {
    if (bulkEditLoading) return;
    setBulkEditLoading(true);
    setBulkEditError('');
    try {
      const payload = bulkEditUseFilter
        ? {
          filter: {
            ...appliedFilters,
            ...appliedAdvancedFilters,
            colorCategory,
          },
          field: bulkEditField,
          value: bulkEditField === 'basePrice' ? Number(bulkEditValue) : bulkEditValue,
        }
        : { itemIds: bulkSelectedIds, field: bulkEditField, value: bulkEditField === 'basePrice' ? Number(bulkEditValue) : bulkEditValue };
      const res = await axios.post('/api/diamonds/bulk-update', payload);
      showSuccess(`Updated ${res.data.updated} item(s).`);
      setBulkEditConfirmOpen(false);
      setBulkEditStep(1);
      setBulkEditOpen(false);
      setBulkSelectedIds([]);
      setBulkEditField('');
      setBulkEditValue('');
      fetchDiamonds();
    } catch (err) {
      setBulkEditError(err.response?.data?.error || 'Bulk update failed');
    } finally {
      setBulkEditLoading(false);
    }
  };
  const closeBulkEdit = () => {
    setBulkEditOpen(false); setBulkSelectedIds([]); setBulkEditStep(1); setBulkEditUseFilter(false);
    setBulkEditField(''); setBulkEditValue(''); setBulkEditConfirmOpen(false);
  };

  // ── Column render map (memoized) ──────────────────────────────────────────

  const columnRenderMap = useMemo(() => ({
    certificateNumber: { render: (row) => <CertificateLink certificateNumber={row.certificateNumber} lab={row.lab} variant="body2" /> },
    sku: { render: (row) => row.sku || '—' },
    shipmentId: { render: (row) => row.shipmentId || '—' },
    shipmentItemID: { render: (row) => row.shipmentItemID || '—' },
    price: { sortable: true, render: (row) => `$${row.price?.toLocaleString()}` },
    buyPricePerCarat: { render: (row) => row.buyPricePerCarat != null ? `$${Number(row.buyPricePerCarat).toLocaleString()}` : '—' },
    totalPrice: { render: (row) => `$${(row.price * row.carat)?.toLocaleString()}` },
    ratio: { render: formatRatio },
    measurements: { render: formatMeasurements },
    carat: { sortable: true },
    color: { render: (row) => (isFancyInventory ? formatFancyColor(row.fancyColor) : (row.color || '—')) },
    cut: { render: (row) => formatGradeShort(row.cut) || '—' },
    polish: { render: (row) => formatGradeShort(row.polish) || '—' },
    symmetry: { render: (row) => formatGradeShort(row.symmetry) || '—' },
    diamondType: { render: (row) => row.diamondType || '—' },
    status: { render: (row) => <Chip label={row.status} color={getStatusColor(row.status)} size="small" /> },
    byWhom: { render: formatByWhom },
    dateAdded: { sortable: isInternal, render: (row) => formatDateTime(row.createdAt) || '—' },
    dateUpdated: { sortable: isInternal, render: (row) => formatDateTime(row.updatedAt) || '—' },
    reserveAction: {
      render: (row) => (
          <Box display="flex" gap={0.5} justifyContent="flex-end" alignItems="center">
            <CertifiedDiamondCartAction diamond={row} canAddToCart={canAddToCart} />
            {canEditDiamond && (
              <Tooltip title={t('inventory.edit')}><IconButton size="small" onClick={() => openEditDiamond(row._id)}><EditIcon fontSize="small" /></IconButton></Tooltip>
            )}
            {canDeleteDiamond && (
              <Tooltip title={t('inventory.delete')}><IconButton size="small" color="error" onClick={() => openDeleteDiamond(row._id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
            )}
            {isInternal && (
              <Tooltip title={t('inventory.auditLog')}>
                <IconButton size="small" onClick={() => openAuditLog('diamond', row._id, row.certificateNumber || 'Diamond')}>
                  <HistoryIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
      ),
    },
  }), [canAddToCart, canEditDiamond, canDeleteDiamond, isInternal, isFancyInventory, openEditDiamond, openDeleteDiamond, openAuditLog, t]);

  const columns = useMemo(() => {
    const alwaysOnColumns = ALL_COLUMNS.filter(c => c.alwaysOn);
    const effectiveColumnConfig = isMobile ? getDefaultColumnConfig() : columnConfig;
    const configurableColumns = effectiveColumnConfig
      ? effectiveColumnConfig.order
          .filter(id => effectiveColumnConfig.visibleIds.includes(id))
          .map(id => {
            const def = ALL_COLUMNS.find(c => c.id === id);
            if (!def || def.alwaysOn) return null;
            if (def.internalOnly && !isInternal) return null;
            if (def.managerOnly && !isManager) return null;
            if (def.requiresPermission && !hasPermission(userRole, def.requiresPermission, customPermissions)) return null;
            return { ...def, label: colLabel(id), ...(columnRenderMap[id] || {}) };
          })
          .filter(Boolean)
      : [];
    const baseColumns = [
      ...configurableColumns,
      ...alwaysOnColumns.filter(c => (!c.internalOnly || isInternal)).map(c => ({ ...c, label: colLabel(c.id), ...(columnRenderMap[c.id] || {}) })),
    ];
    return bulkEditOpen && isInternal && isManager
      ? [
          {
            id: '_select',
            label: '',
            render: (row) => (
              <Checkbox
                size="small"
                checked={bulkSelectedIds.includes(row._id)}
                onChange={(e) => {
                  if (e.target.checked) setBulkSelectedIds(prev => [...prev, row._id]);
                  else setBulkSelectedIds(prev => prev.filter(id => id !== row._id));
                }}
              />
            ),
          },
          ...baseColumns,
        ]
      : baseColumns;
  }, [isMobile, columnConfig, columnRenderMap, isInternal, isManager, userRole, customPermissions, getDefaultColumnConfig, bulkEditOpen, bulkSelectedIds, colLabel]);

  // ── Mobile card renderer (memoized) ───────────────────────────────────────

  const renderMobileCard = useCallback((diamond) => {
    const expanded = mobileExpandedCardIds.has(diamond._id);
    const ratioVal = diamond.ratio != null ? diamond.ratio
      : (diamond.length != null && diamond.width != null && Number(diamond.width) > 0)
        ? (Math.round((diamond.length / diamond.width) * 100) / 100).toFixed(2) : null;
    const measurementsStr = formatMeasurements(diamond) || '—';
    const colorLabel = isFancyInventory ? formatFancyColor(diamond.fancyColor) : diamond.color;
    const primaryLine = [diamond.shape, diamond.carat, colorLabel, diamond.clarity]
      .filter((v) => v != null && v !== '')
      .join(' ');
    const pricePerCarat = diamond.price != null && diamond.price !== '' ? Number(diamond.price) : null;
    const totalStonePrice =
      pricePerCarat != null && !Number.isNaN(pricePerCarat) && diamond.carat != null && !Number.isNaN(Number(diamond.carat))
        ? pricePerCarat * Number(diamond.carat) : null;

    const actionButtons = (
      <Box display="flex" alignItems="center" gap={0.25} flexShrink={0}>
        <CertifiedDiamondCartAction diamond={diamond} canAddToCart={canAddToCart} />
        {canEditDiamond && (
          <Tooltip title={t('inventory.edit')}>
            <IconButton size="small" onClick={() => openEditDiamond(diamond._id)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {canDeleteDiamond && (
          <Tooltip title={t('inventory.delete')}>
            <IconButton size="small" color="error" onClick={() => openDeleteDiamond(diamond._id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {isInternal && (
          <Tooltip title={t('inventory.auditLog')}>
            <IconButton
              size="small"
              onClick={() => openAuditLog('diamond', diamond._id, diamond.certificateNumber || 'Diamond')}
            >
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );

    return (
      <Box sx={{ '& .MuiTypography-root': { lineHeight: 1.2 } }}>
        <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
          {bulkEditOpen && isInternal && isManager && (
            <Checkbox
              size="small"
              checked={bulkSelectedIds.includes(diamond._id)}
              onChange={(e) => {
                e.stopPropagation();
                if (e.target.checked) setBulkSelectedIds((prev) => [...prev, diamond._id]);
                else setBulkSelectedIds((prev) => prev.filter((id) => id !== diamond._id));
              }}
              sx={{ p: 0.25 }}
            />
          )}
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
              {primaryLine || '—'}
            </Typography>
            {diamond.certificateNumber ? (
              <CertificateLink
                certificateNumber={diamond.certificateNumber}
                lab={diamond.lab}
                variant="body2"
                sx={{ fontSize: '0.85rem', fontWeight: 600, wordBreak: 'break-all' }}
              />
            ) : null}
          </Box>
          <IconButton
            size="small"
            onClick={() => toggleMobileCardExpanded(diamond._id)}
            aria-label={expanded ? t('inventory.showLess') : t('inventory.showMore')}
            aria-expanded={expanded}
            sx={{ p: 0.25 }}
          >
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Box>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={0.5}>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 0, fontSize: '0.8rem' }}>
            {measurementsStr}
            {ratioVal != null ? ` Ratio: ${ratioVal}` : ''}
          </Typography>
          {(pricePerCarat != null || totalStonePrice != null) && (
            <Box display="flex" alignItems="baseline" gap={0.5} flexShrink={0}>
              {pricePerCarat != null && (
                <Typography variant="caption" sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'primary.main' }}>
                  ${pricePerCarat.toLocaleString()}/ct
                </Typography>
              )}
              {totalStonePrice != null && (
                <Typography variant="caption" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                  (${totalStonePrice.toLocaleString(undefined, { maximumFractionDigits: 0 })})
                </Typography>
              )}
            </Box>
          )}
          {actionButtons}
        </Box>
        {expanded && (
        <Grid container spacing={0.5} sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
          <Grid item xs={12}>
            <Box display="flex" flexWrap="wrap" justifyContent="space-between" alignItems="flex-start" gap={1} mb={0.5}>
              <Box sx={{ minWidth: 0, flex: '1 1 160px' }}>
                <CertificateLink certificateNumber={diamond.certificateNumber} lab={diamond.lab} variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', wordBreak: 'break-all' }} />
              </Box>
              {isInternal && (
                <Chip label={diamond.status} color={getStatusColor(diamond.status)} size="small" sx={{ height: 22, fontSize: '0.75rem', flexShrink: 0 }} />
              )}
            </Box>
          </Grid>
          {isInternal && diamond.sku ? (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" sx={CARD_LABEL_SX}>SKU</Typography>
              <Typography variant="body2" sx={CARD_VALUE_SX}>{diamond.sku}</Typography>
            </Grid>
          ) : null}
          {isInternal && diamond.shipmentId ? (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" sx={CARD_LABEL_SX}>Shipment ID</Typography>
              <Typography variant="body2" sx={CARD_VALUE_SX}>{diamond.shipmentId}</Typography>
            </Grid>
          ) : null}
          {isInternal && diamond.shipmentItemID ? (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" sx={CARD_LABEL_SX}>Shipment Item ID</Typography>
              <Typography variant="body2" sx={CARD_VALUE_SX}>{diamond.shipmentItemID}</Typography>
            </Grid>
          ) : null}
          <Grid item xs={4}><Typography variant="caption" color="text.secondary" sx={CARD_LABEL_SX}>Shape</Typography><Typography variant="body2" sx={CARD_VALUE_SX}>{diamond.shape}</Typography></Grid>
          <Grid item xs={4}><Typography variant="caption" color="text.secondary" sx={CARD_LABEL_SX}>Carat</Typography><Typography variant="body2" sx={CARD_VALUE_SX}>{diamond.carat}</Typography></Grid>
          <Grid item xs={4}><Typography variant="caption" color="text.secondary" sx={CARD_LABEL_SX}>Type</Typography><Typography variant="body2" sx={CARD_VALUE_SX}>{diamond.diamondType || '—'}</Typography></Grid>
          <Grid item xs={4}><Typography variant="caption" color="text.secondary" sx={CARD_LABEL_SX}>{isFancyInventory ? t('inventory.fancyColor') : t('inventory.col.color')}</Typography><Typography variant="body2" sx={CARD_VALUE_SX}>{isFancyInventory ? formatFancyColor(diamond.fancyColor) : diamond.color}</Typography></Grid>
          <Grid item xs={4}><Typography variant="caption" color="text.secondary" sx={CARD_LABEL_SX}>Clarity</Typography><Typography variant="body2" sx={CARD_VALUE_SX}>{diamond.clarity}</Typography></Grid>
          <Grid item xs={4}><Typography variant="caption" color="text.secondary" sx={CARD_LABEL_SX}>Lab</Typography><Typography variant="body2" sx={CARD_VALUE_SX}>{diamond.lab}</Typography></Grid>
          <Grid item xs={4}><Typography variant="caption" color="text.secondary" sx={CARD_LABEL_SX}>Cut</Typography><Typography variant="body2" sx={CARD_VALUE_SX}>{formatGradeShort(diamond.cut) || '—'}</Typography></Grid>
          <Grid item xs={4}><Typography variant="caption" color="text.secondary" sx={CARD_LABEL_SX}>Symmetry</Typography><Typography variant="body2" sx={CARD_VALUE_SX}>{formatGradeShort(diamond.symmetry) || '—'}</Typography></Grid>
          <Grid item xs={4}><Typography variant="caption" color="text.secondary" sx={CARD_LABEL_SX}>Polish</Typography><Typography variant="body2" sx={CARD_VALUE_SX}>{formatGradeShort(diamond.polish) || '—'}</Typography></Grid>
          <Grid item xs={12}>
            <Box display="flex" alignItems="baseline" gap={1.5} flexWrap="wrap">
              <Box>
                <Typography variant="caption" color="text.secondary" sx={CARD_LABEL_SX}>Measurements (mm)</Typography>
                <Typography variant="body2" sx={{ ...CARD_VALUE_SX, fontSize: '0.85rem' }}>
                  {(diamond.length != null || diamond.width != null || diamond.height != null)
                    ? ([diamond.length, diamond.width, diamond.height].every(n => n != null)
                      ? `${diamond.length} × ${diamond.width} × ${diamond.height}`
                      : [diamond.length, diamond.width, diamond.height].filter(Boolean).join(' × '))
                    : '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={CARD_LABEL_SX}>Ratio</Typography>
                <Typography variant="body2" sx={{ ...CARD_VALUE_SX, fontSize: '0.85rem' }}>{ratioVal ?? '—'}</Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="caption" color="text.secondary" sx={CARD_LABEL_SX}>$/ct</Typography>
              <Typography variant="body2" sx={{ ...CARD_VALUE_SX, fontWeight: 'bold', color: 'primary.main' }}>
                {pricePerCarat != null && !Number.isNaN(pricePerCarat) ? `$${pricePerCarat.toLocaleString()}` : '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={CARD_LABEL_SX}>Total</Typography>
              <Typography variant="body2" sx={CARD_VALUE_SX}>
                {totalStonePrice != null && !Number.isNaN(totalStonePrice) ? `$${totalStonePrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '—'}
              </Typography>
            </Box>
          </Grid>
          {isInternal && (diamond.soldByUser || diamond.reservedByUser || diamond.requestedByUser) && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" sx={CARD_LABEL_SX}>By whom</Typography>
              <Typography variant="body2" sx={{ ...CARD_VALUE_SX, fontSize: '0.85rem' }}>
                {diamond.status === 'sold' && diamond.soldByUser
                  ? `${diamond.soldByUser} → ${diamond.soldToCompany || ''}`
                  : diamond.status === 'reserved' && diamond.reservedByUser
                    ? `${diamond.reservedByUser} (${diamond.reservedForCompany || ''})`
                    : diamond.requestedByUser
                      ? `Requested: ${diamond.requestedByUser} (${diamond.requestedForCompany || ''})`
                      : ''}
              </Typography>
            </Grid>
          )}
        </Grid>
        )}
      </Box>
    );
  }, [
    mobileExpandedCardIds,
    toggleMobileCardExpanded,
    bulkEditOpen,
    isManager,
    bulkSelectedIds,
    isInternal,
    canAddToCart,
    canEditDiamond,
    canDeleteDiamond,
    openEditDiamond,
    openDeleteDiamond,
    openAuditLog,
    t,
    isFancyInventory,
  ]);

  // ── Filter badge counts ───────────────────────────────────────────────────

  const certifiedFiltersActiveCount = useMemo(() => {
    let n = 0;
    const st = appliedFilters.status;
    if (Array.isArray(st)) { if (st.length === 0) n += 1; else if (st.length > 1 || st[0] !== 'available') n += 1; }
    if (isInternal && appliedFilters.sku?.trim()) n += 1;
    if (appliedFilters.certificateNumber?.trim()) n += 1;
    if (appliedFilters.shipmentId?.trim()) n += 1;
    if (appliedFilters.shipmentItemID?.trim()) n += 1;
    if (appliedFilters.shape) n += 1;
    if (!isFancyInventory && appliedFilters.color?.length) n += 1;
    if (isFancyInventory && appliedFilters.fancyIntensity?.length) n += 1;
    if (isFancyInventory && appliedFilters.fancyOvertone?.length) n += 1;
    if (appliedFilters.clarity?.length) n += 1;
    if (appliedFilters.minCarat) n += 1;
    if (appliedFilters.maxCarat) n += 1;
    if (appliedFilters.cut) n += 1;
    if (appliedFilters.polish) n += 1;
    if (appliedFilters.symmetry) n += 1;
    if (appliedAdvancedFilters.minRatio || appliedAdvancedFilters.maxRatio || appliedAdvancedFilters.minLength || appliedAdvancedFilters.maxLength || appliedAdvancedFilters.minWidth || appliedAdvancedFilters.maxWidth) n += 1;
    return n;
  }, [appliedFilters, appliedAdvancedFilters, isInternal, isFancyInventory]);

  const certifiedDrawerBadgeCount = useMemo(() => {
    if (!isInternal) return certifiedFiltersActiveCount;
    let n = certifiedFiltersActiveCount;
    if (appliedFilters.certificateNumber?.trim()) n -= 1;
    return n;
  }, [certifiedFiltersActiveCount, isInternal, appliedFilters.certificateNumber]);

  // ── Active filter chips ───────────────────────────────────────────────────
  //
  // Each chip represents one applied filter value. Clicking the chip's ×
  // removes that filter and immediately re-fetches so the table updates.

  // Chips are derived from appliedFilters/appliedAdvancedFilters (the last
  // searched snapshot) so they only appear after the user clicks Search.
  const activeFilterChips = useMemo(() => {
    const chips = [];
    if (isInternal && appliedFilters.certificateNumber?.trim())
      chips.push({ key: 'certificateNumber', label: `Cert #: ${appliedFilters.certificateNumber}` });
    if (appliedFilters.shape)
      chips.push({ key: 'shape', label: `Shape: ${appliedFilters.shape}` });
    if (!isFancyInventory && appliedFilters.color?.length)
      chips.push({ key: 'color', label: `Color: ${appliedFilters.color.join(', ')}` });
    if (isFancyInventory && appliedFilters.fancyIntensity?.length)
      chips.push({ key: 'fancyIntensity', label: `Intensity: ${appliedFilters.fancyIntensity.join(', ')}` });
    if (isFancyInventory && appliedFilters.fancyHue?.length)
      chips.push({ key: 'fancyHue', label: `Hue: ${appliedFilters.fancyHue.join(', ')}` });
    if (isFancyInventory && appliedFilters.fancyOvertone?.length)
      chips.push({ key: 'fancyOvertone', label: `Overtone: ${appliedFilters.fancyOvertone.join(', ')}` });
    if (appliedFilters.clarity?.length)
      chips.push({ key: 'clarity', label: `Clarity: ${appliedFilters.clarity.join(', ')}` });
    if (appliedFilters.minCarat || appliedFilters.maxCarat) {
      const label = appliedFilters.minCarat && appliedFilters.maxCarat
        ? `Carat: ${appliedFilters.minCarat}–${appliedFilters.maxCarat}`
        : appliedFilters.minCarat ? `Min carat: ${appliedFilters.minCarat}` : `Max carat: ${appliedFilters.maxCarat}`;
      chips.push({ key: 'carat', label });
    }
    if (appliedFilters.cut) chips.push({ key: 'cut', label: `Cut: ${appliedFilters.cut}` });
    if (appliedFilters.polish) chips.push({ key: 'polish', label: `Polish: ${appliedFilters.polish}` });
    if (appliedFilters.symmetry) chips.push({ key: 'symmetry', label: `Symmetry: ${appliedFilters.symmetry}` });
    if (isInternal) {
      const isDefaultStatus = appliedFilters.status.length === 1 && appliedFilters.status[0] === 'available';
      if (!isDefaultStatus && appliedFilters.status.length > 0) {
        const statusLabel = appliedFilters.status.map((s) => DIAMOND_STATUS_OPTIONS.find((o) => o.value === s)?.label || s).join(', ');
        chips.push({ key: 'status', label: `Status: ${statusLabel}` });
      }
      if (appliedFilters.sku?.trim()) chips.push({ key: 'sku', label: `SKU: ${appliedFilters.sku}` });
      if (appliedFilters.shipmentId?.trim()) chips.push({ key: 'shipmentId', label: `Shipment: ${appliedFilters.shipmentId}` });
      if (appliedFilters.shipmentItemID?.trim()) chips.push({ key: 'shipmentItemID', label: `Shipment Item: ${appliedFilters.shipmentItemID}` });
    }
    if (appliedAdvancedFilters.minRatio || appliedAdvancedFilters.maxRatio) {
      const label = appliedAdvancedFilters.minRatio && appliedAdvancedFilters.maxRatio
        ? `Ratio: ${appliedAdvancedFilters.minRatio}–${appliedAdvancedFilters.maxRatio}`
        : appliedAdvancedFilters.minRatio ? `Min ratio: ${appliedAdvancedFilters.minRatio}` : `Max ratio: ${appliedAdvancedFilters.maxRatio}`;
      chips.push({ key: 'ratio', label });
    }
    if (appliedAdvancedFilters.minLength || appliedAdvancedFilters.maxLength) {
      const label = appliedAdvancedFilters.minLength && appliedAdvancedFilters.maxLength
        ? `Length: ${appliedAdvancedFilters.minLength}–${appliedAdvancedFilters.maxLength} mm`
        : appliedAdvancedFilters.minLength ? `Min length: ${appliedAdvancedFilters.minLength} mm` : `Max length: ${appliedAdvancedFilters.maxLength} mm`;
      chips.push({ key: 'length', label });
    }
    if (appliedAdvancedFilters.minWidth || appliedAdvancedFilters.maxWidth) {
      const label = appliedAdvancedFilters.minWidth && appliedAdvancedFilters.maxWidth
        ? `Width: ${appliedAdvancedFilters.minWidth}–${appliedAdvancedFilters.maxWidth} mm`
        : appliedAdvancedFilters.minWidth ? `Min width: ${appliedAdvancedFilters.minWidth} mm` : `Max width: ${appliedAdvancedFilters.maxWidth} mm`;
      chips.push({ key: 'width', label });
    }
    return chips;
  }, [appliedFilters, appliedAdvancedFilters, isInternal, isFancyInventory]);

  const handleRemoveFilterChip = useCallback((key) => {
    let filterUpdates = {};
    let advancedUpdates = null;
    switch (key) {
      case 'certificateNumber': filterUpdates = { certificateNumber: '' }; setCertNumberInput(''); break;
      case 'shape':
        filterUpdates = { shape: isClient ? clientDefaultShape : '' };
        setQfShape(isClient ? clientDefaultShape : '');
        break;
      case 'color':      filterUpdates = { color: [] }; break;
      case 'fancyIntensity': filterUpdates = { fancyIntensity: [] }; break;
      case 'fancyHue':   filterUpdates = { fancyHue: [] }; break;
      case 'fancyOvertone': filterUpdates = { fancyOvertone: [] }; break;
      case 'clarity':    filterUpdates = { clarity: [] }; break;
      case 'carat':      filterUpdates = { minCarat: '', maxCarat: '' }; setQfWeight(''); break;
      case 'cut':        filterUpdates = { cut: '' }; break;
      case 'polish':     filterUpdates = { polish: '' }; break;
      case 'symmetry':   filterUpdates = { symmetry: '' }; break;
      case 'status':     filterUpdates = { status: ['available'] }; break;
      case 'sku':        filterUpdates = { sku: '' }; break;
      case 'shipmentId': filterUpdates = { shipmentId: '' }; break;
      case 'shipmentItemID': filterUpdates = { shipmentItemID: '' }; break;
      case 'ratio':      advancedUpdates = { minRatio: '', maxRatio: '' }; break;
      case 'length':     advancedUpdates = { minLength: '', maxLength: '' }; setQfLength(''); break;
      case 'width':      advancedUpdates = { minWidth: '', maxWidth: '' }; break;
      default: return;
    }
    // Base the new state off the applied snapshot (what the table is actually showing),
    // then sync both the live filters and the applied snapshot so the drawer and
    // chips stay consistent.
    const newFilters = { ...appliedFilters, ...filterUpdates };
    const newAdvanced = advancedUpdates ? { ...appliedAdvancedFilters, ...advancedUpdates } : appliedAdvancedFilters;
    setFilters(newFilters);
    setAppliedFilters(newFilters);
    setAdvancedFilters(newAdvanced);
    setAppliedAdvancedFilters(newAdvanced);
    setPage(0);
    const snap = { filters: newFilters, advancedFilters: newAdvanced, sortBy, sortOrder, page: 0, rowsPerPage, isInternal, defaultRows: isMobile ? 20 : 50 };
    skipUrlHydrationRef.current = true;
    setSearchParams(buildCertifiedSearchParams(snap), { replace: true });
    fetchDiamonds({ pageOverride: 0, filtersOverride: newFilters, advancedFiltersOverride: newAdvanced });
  }, [appliedFilters, appliedAdvancedFilters, sortBy, sortOrder, rowsPerPage, isInternal, isMobile, isClient, clientDefaultShape, fetchDiamonds, setSearchParams]);

  // ── Main render ───────────────────────────────────────────────────────────

  return (
    <>
      {/* Header actions row */}
      <Box display="flex" gap={1} justifyContent="flex-end" mb={2} flexDirection={isMobile ? 'column' : 'row'} width={isMobile ? '100%' : 'auto'}>
        {!isMobile && (
          <Button variant="outlined" startIcon={<ViewColumnIcon />} onClick={() => setColumnDrawerOpen(true)} size="medium">
            {t('inventory.columns')}
          </Button>
        )}
        {canAddDiamond && (
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setAddDiamondDialogOpen(true)} size={isMobile ? 'small' : 'medium'} fullWidth={isMobile}>
            {t('inventory.addDiamond')}
          </Button>
        )}
        {isManager && !isMobile && (
          <Button variant="contained" startIcon={<UploadIcon />} onClick={() => setUploadDialogOpen(true)} size="medium">
            {t('inventory.uploadCsv')}
          </Button>
        )}
      </Box>

      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
        <Box
          component={isInternal ? 'form' : 'div'}
          onSubmit={isInternal ? (e) => { e.preventDefault(); handleSearch(); } : undefined}
          sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}
        >
          {isInternal && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap', width: '100%', minWidth: 0 }}>
              <TextField
                size="small" label={t('inventory.certificateHash')} value={certNumberInput}
                onChange={(e) => setCertNumberInput(e.target.value)}
                placeholder="Contains…" sx={{ flex: 1, minWidth: 0 }}
                inputProps={{ 'aria-label': 'Filter by certificate number' }}
              />
              <Button type="submit" variant="contained" color="primary" startIcon={<SearchIcon />} size="medium" sx={{ flexShrink: 0 }}>
                {t('inventory.search')}
              </Button>
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%', minWidth: 0 }}>
            <Tooltip title="Search filters">
              <IconButton color="primary" onClick={() => setFiltersDrawerOpen(true)} aria-label="Open search filters" size="large" type="button" sx={{ flexShrink: 0 }}>
                <Badge color="error" badgeContent={certifiedDrawerBadgeCount} invisible={certifiedDrawerBadgeCount === 0}>
                  <FilterListIcon />
                </Badge>
              </IconButton>
            </Tooltip>
            {isFancyInventory && (
              <SelectField
                label={t('inventory.fancyColor')}
                multiple
                value={appliedFilters.fancyHue}
                onChange={handleFancyHueFilterChange}
                options={fancyHueFilterOptions}
                emptyLabel="All"
                sx={{ minWidth: { xs: '100%', sm: 180 }, flexShrink: 0 }}
              />
            )}
            <QuickFilterBar
              shapes={filterShapeList}
              weightLabel="Carat"
              weightTolerance={0.20}
              lengthTolerance={0.50}
              shapeValue={qfShape}
              weightCenterValue={qfWeight}
              lengthCenterValue={qfLength}
              includeAllShapeOption={!isClient}
              defaultShape={clientDefaultShape}
              onApply={handleQuickCertFilterApply}
              onClear={handleQuickCertFilterClear}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {isInternal ? t('inventory.filterHintInternal') : t('inventory.filterHintClient')}
          </Typography>
          {activeFilterChips.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {activeFilterChips.map((chip) => (
                <Chip
                  key={chip.key}
                  label={chip.label}
                  size="small"
                  onDelete={isClient && chip.key === 'shape' ? undefined : () => handleRemoveFilterChip(chip.key)}
                />
              ))}
            </Box>
          )}
        </Box>

        {bulkEditOpen && isInternal && isManager && (
          <Box sx={{ mt: !isMobile ? 2 : 1.5, p: 2, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            {bulkEditError && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setBulkEditError('')}>{bulkEditError}</Alert>}
            {bulkEditStep === 1 ? (
              <>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>{t('inventory.bulkEditStep1Title')}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('inventory.bulkEditStep1Desc')}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                  <Button size="small" variant="outlined" onClick={() => setBulkSelectedIds(diamonds.map(d => d._id))}>{t('inventory.selectAllOnPage')}</Button>
                  <Button size="small" variant="outlined" onClick={() => setBulkSelectedIds([])}>{t('inventory.clearSelection')}</Button>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>({bulkSelectedIds.length} selected)</Typography>
                  <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
                  <Button size="small" variant="contained" onClick={handleBulkEditUseFilter}>
                    {t('inventory.updateAllMatching', { count: total })}
                  </Button>
                  <Box sx={{ flex: 1, minWidth: 80 }} />
                  <Button size="small" variant="contained" onClick={handleBulkEditNext} disabled={!bulkEditUseFilter && bulkSelectedIds.length === 0}>
                    {t('inventory.nextSetPriceOrStatus')}
                  </Button>
                  <Button size="small" onClick={closeBulkEdit}>{t('inventory.cancel')}</Button>
                </Box>
              </>
            ) : (
              <>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>{t('inventory.bulkEditStep2Title')}</Typography>
                <Typography variant="body2" color="text.secondary">{bulkEditCount} item(s) will be updated. Use the dialog to choose the new value, then confirm.</Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                  <Button size="small" variant="outlined" onClick={() => { setBulkEditStep(1); setBulkEditField(''); setBulkEditValue(''); }}>{t('inventory.back')}</Button>
                  <Button size="small" onClick={closeBulkEdit}>{t('inventory.cancel')}</Button>
                </Box>
              </>
            )}
          </Box>
        )}
      </Paper>

      {/* Table */}
      <Box sx={{ position: 'relative', minHeight: diamondsLoading ? 220 : undefined }} aria-busy={diamondsLoading}>
        {diamondsLoading && (
          <Box sx={{ position: 'absolute', inset: 0, zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.72)'), backdropFilter: 'blur(2px)', borderRadius: 1 }}>
            <CircularProgress />
          </Box>
        )}
        <Box sx={{ opacity: diamondsLoading ? 0.35 : 1, transition: 'opacity 0.2s ease', pointerEvents: diamondsLoading ? 'none' : 'auto' }}>
          <ResponsiveTable
            columns={columns}
            data={diamonds}
            renderMobileCard={renderMobileCard}
            getStatusColor={getStatusColor}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            sortColumnFilter={(id) => isCertifiedSortColumn(id, isInternal)}
            defaultSortBy={isInternal ? DEFAULT_INTERNAL_CERTIFIED_SORT : DEFAULT_CLIENT_CERTIFIED_SORT}
            defaultSortOrder="desc"
            onResetSort={() => handleSort(isInternal ? DEFAULT_INTERNAL_CERTIFIED_SORT : DEFAULT_CLIENT_CERTIFIED_SORT, 'desc')}
            tableMaxHeight={isMobile ? 'none' : VIEWPORT_TABLE_MAX_HEIGHT}
          />
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(e, newPage) => {
              setPage(newPage);
              queueMicrotask(() => {
                skipUrlHydrationRef.current = true;
                setSearchParams(buildCertifiedSearchParams({ ...getUrlSnapshot(), page: newPage }), { replace: true });
              });
            }}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              const next = parseInt(e.target.value, 10);
              setRowsPerPage(next);
              setPage(0);
              saveTableRowsPreference(TABLE_ROWS_PREF_KEYS.inventoryCertified, next).catch(() => {});
              queueMicrotask(() => {
                skipUrlHydrationRef.current = true;
                setSearchParams(buildCertifiedSearchParams({ ...getUrlSnapshot(), page: 0, rowsPerPage: next }), { replace: true });
              });
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
            sx={{ '& .MuiTablePagination-toolbar': { flexWrap: 'wrap' }, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: { xs: '0.75rem', sm: '0.875rem' } } }}
          />
        </Box>
      </Box>

      <CertifiedFilterDrawer
        open={filtersDrawerOpen}
        onClose={() => setFiltersDrawerOpen(false)}
        appliedFilters={appliedFilters}
        appliedAdvancedFilters={appliedAdvancedFilters}
        filterOptions={filterOptionsForUi}
        isInternal={isInternal}
        isManager={isManager}
        onSearch={handleSearch}
        onClearAll={handleClearFilters}
        onBulkEdit={handleOpenBulkEditFromFilters}
        includeAllShapeOption={!isClient}
        defaultShape={clientDefaultShape}
        colorCategory={colorCategory}
        hideFancyHueFilter={isFancyInventory}
      />

      {/* Upload CSV dialog */}
      <UploadCsvDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        defaultUploadType={isFancyInventory ? 'certifiedFancy' : 'certified'}
        onImportSuccess={() => handleSearch()}
      />

      <EditDiamondDialog
        open={!!editDiamondId}
        diamondId={editDiamondId}
        onClose={closeEditDiamond}
        onSaved={fetchDiamonds}
        formShapeList={formShapeList}
        filterOptions={filterOptions}
        isInternal={isInternal}
        canSeeBuyPrices={canSeeBuyPrices}
        isMobile={isMobile}
        colorCategory={colorCategory}
      />

      <AddDiamondDialog
        open={addDiamondDialogOpen}
        onClose={() => setAddDiamondDialogOpen(false)}
        onSaved={handleAddDiamondSaved}
        formShapeList={formShapeList}
        filterOptions={filterOptions}
        isInternal={isInternal}
        canSeeBuyPrices={canSeeBuyPrices}
        isMobile={isMobile}
        colorCategory={colorCategory}
      />

      {/* Delete diamond confirmation */}
      <Dialog open={deleteDiamondConfirmOpen} onClose={closeDeleteDiamond}>
        <DialogTitle>{t('inventory.deleteDiamond')}</DialogTitle>
        <DialogContent>
          {deleteDiamondError && <Alert severity="error" sx={{ mb: 2 }}>{deleteDiamondError}</Alert>}
          <Typography>{t('inventory.deleteConfirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDiamond}>{t('inventory.cancel')}</Button>
          <Button onClick={handleConfirmDeleteDiamond} color="error" variant="contained" disabled={deleteDiamondLoading}>{t('inventory.delete')}</Button>
        </DialogActions>
      </Dialog>

      {/* Bulk edit – step 2: choose field and value */}
      <Dialog
        open={bulkEditOpen && bulkEditStep === 2}
        onClose={() => { setBulkEditStep(1); setBulkEditField(''); setBulkEditValue(''); }}
        maxWidth="sm" fullWidth fullScreen={isMobile}
      >
        <DialogTitle>{t('inventory.bulkEditDialogTitle')}</DialogTitle>
        <DialogContent>
          {bulkEditError && <Alert severity="error" sx={{ mb: 2 }}>{bulkEditError}</Alert>}
          <RadioGroup row value={bulkEditField} onChange={(e) => { setBulkEditField(e.target.value); setBulkEditValue(e.target.value === 'basePrice' ? '' : (DIAMOND_STATUS_OPTIONS[0]?.value ?? '')); }} sx={{ mb: 2 }}>
            <FormControlLabel value="basePrice" control={<Radio />} label={t('inventory.col.pricePerCarat')} />
            <FormControlLabel value="status" control={<Radio />} label={t('inventory.col.status')} />
          </RadioGroup>
          {bulkEditField === 'basePrice' && <TextField fullWidth label="Price per ct" type="number" value={bulkEditValue} onChange={(e) => setBulkEditValue(e.target.value)} inputProps={{ step: 0.01, min: 0 }} />}
          {bulkEditField === 'status' && (
            <SelectField
              label="Status"
              fullWidth
              value={bulkEditValue}
              onChange={(next) => setBulkEditValue(next)}
              options={DIAMOND_STATUS_OPTIONS}
              emptyLabel=""
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setBulkEditStep(1); setBulkEditField(''); setBulkEditValue(''); setBulkEditError(''); }}>{t('inventory.back')}</Button>
          <Button onClick={closeBulkEdit}>{t('inventory.cancel')}</Button>
          <Button variant="contained" onClick={handleBulkEditFieldConfirm} disabled={!bulkEditField || (bulkEditField === 'basePrice' && (bulkEditValue === '' || Number(bulkEditValue) < 0)) || (bulkEditField === 'status' && !bulkEditValue)}>
            {t('inventory.nextConfirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk edit – step 3: confirmation */}
      <Dialog open={bulkEditConfirmOpen} onClose={() => setBulkEditConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('inventory.confirmBulkUpdate')}</DialogTitle>
        <DialogContent>
          <Typography>{t('inventory.confirmUpdateItems', { count: bulkEditCount })}</Typography>
          <Typography sx={{ mt: 1 }}>
            <strong>{bulkEditField === 'basePrice' ? t('inventory.col.pricePerCarat') : t('inventory.col.status')}:</strong>{' '}
            {bulkEditField === 'basePrice' ? String(bulkEditValue) : (DIAMOND_STATUS_OPTIONS.find((o) => o.value === bulkEditValue)?.label ?? bulkEditValue)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkEditConfirmOpen(false)}>{t('inventory.cancel')}</Button>
          <Button variant="contained" onClick={handleBulkEditSubmit} disabled={bulkEditLoading}>{bulkEditLoading ? t('inventory.updating') : t('inventory.update')}</Button>
        </DialogActions>
      </Dialog>

      {/* Audit log dialog */}
      <AuditLogDialog
        open={auditDialogOpen}
        onClose={() => { setAuditDialogOpen(false); setAuditLogs([]); setAuditTitle(''); }}
        logs={auditLogs}
        loading={auditLoading}
        title={auditTitle}
      />

      {/* Column configuration drawer */}
      <Drawer
        anchor="right"
        open={columnDrawerOpen}
        onClose={() => setColumnDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 360 } } }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={() => setColumnDrawerOpen(false)} edge="start" sx={{ display: { sm: 'none' } }}>
              <CloseIcon />
            </IconButton>
            <Typography variant="h6">{t('inventory.configureColumns')}</Typography>
          </Box>
          <Button size="small" onClick={handleResetColumns}>{t('inventory.reset')}</Button>
        </Box>
        <Divider />
        <Typography variant="caption" color="text.secondary" sx={{ px: 2, pt: 1 }}>
          {t('inventory.dragToReorder')}
        </Typography>
        <List dense sx={{ pt: 0 }}>
          {columnConfig && columnConfig.order
            .filter(id => {
              const def = ALL_COLUMNS.find(c => c.id === id);
              if (!def) return false;
              if (def.alwaysOn) return false;
              if (def.internalOnly && !isInternal) return false;
              return true;
            })
            .map((colId) => {
              const def = ALL_COLUMNS.find(c => c.id === colId);
              const isVisible = columnConfig.visibleIds.includes(colId);
              return (
                <ListItem
                  key={colId}
                  draggable
                  onDragStart={() => handleDragStart(colId)}
                  onDragOver={(e) => handleDragOver(e, colId)}
                  onDragEnd={handleDragEnd}
                  sx={{ cursor: 'grab', bgcolor: dragIdx === colId ? 'action.hover' : 'transparent', '&:hover': { bgcolor: 'action.hover' }, opacity: isVisible ? 1 : 0.5, pr: 8 }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}><DragIndicatorIcon fontSize="small" color="action" /></ListItemIcon>
                  <ListItemText primary={def?.label || colId} primaryTypographyProps={{ variant: 'body2' }} />
                  <ListItemSecondaryAction>
                    <Switch edge="end" size="small" checked={isVisible} onChange={() => handleToggleColumn(colId)} />
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })
          }
        </List>
      </Drawer>
    </>
  );
};

export default React.memo(CertifiedInventory, certifiedInventoryPropsAreEqual);
